/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Price } from '@thd-olt-component-react/price';
import {
  number, string, bool, shape, func,
  oneOfType, node
} from 'prop-types';
import { Promotion } from '@thd-olt-component-react/promotion';
import { TileContent, useBreakpoint } from '@one-thd/sui-atomic-components';
import {
  ProductPod as ProductPodOlt,
  ProductATC, ProductBadge,
  PodSticker, PodFooter, PodSection, PodSpacer,
} from '@thd-olt-component-react/product-pod';
import { getHasConditionalPromotions } from '../../utils/cabinet-utils';

import { Image } from './sub-components/Image';
import { Header } from './sub-components/Header';
import { ProductPodDataModel } from './dataModel';

const defaultPresentation = {
  badge: {
    enabled: false,
  },
  media: {
    enabled: true,
    aspect: 'square',
    padding: false,
    fetchPriority: 'auto',
    loading: false,
    swatch: false,
    src: null,
    altText: null,
    height: 200,
    width: 200,
    children: () => null,
  },
  header: {
    enabled: true,
    brandMaxLine: 1,
    brandTitleMaxLine: 1,
    titleMaxLine: 2,
    header: null,
    subheader: null,
    headerWeight: 'bold',
    padding: false,
    override: null,
    isInline: false
  },
  price: {
    enabled: true,
    hideLimitPerOrder: false
  },
  promotion: {
    enabled: true
  },
  sample: {
    enabled: true
  },
  cta: {
    enabled: () => false,
  }
};

export const Pod = ({
  itemId,
  storeId,
  index,
  product,
  channel,
  presentation: presentationProp,
  variant = 'card',
  children = null,
  ctaComponent = null,
  onSelectChoice = () => {},
}) => {
  const matches = useBreakpoint('md');
  const isMultiColumn = matches.greater; // true for tablet (md) and desktop (lg+)

  const presentation = {
    ...defaultPresentation,
    ...presentationProp,
    badge: { ...defaultPresentation.badge, ...presentationProp?.badge },
    media: { ...defaultPresentation.media, ...presentationProp?.media },
    header: { ...defaultPresentation.header, ...presentationProp?.header },
    price: { ...defaultPresentation.price, ...presentationProp?.price },
    promotion: { ...defaultPresentation.promotion, ...presentationProp?.promotion },
    sample: { ...defaultPresentation.sample, ...presentationProp?.sample },
    cta: { ...defaultPresentation.cta, ...presentationProp?.cta },
  };
  const hasConditionalPromotions = getHasConditionalPromotions({ product });

  return (
    <ProductPodOlt
      itemId={itemId}
      analyticsData={{
        parent: 'plp', position: index
      }}
      render={(pod) => (
        <div role="button" tabIndex={0} onClick={onSelectChoice}>
          <PodSection noPadding>
            {/* Product Pod - Product Badge */}
            {presentation.badge.enabled && (
              <PodSpacer minHeight={isMultiColumn ? '2.4rem' : '0'}>
                <PodSticker>
                  <ProductBadge itemId={pod.itemId} storeId={storeId} />
                </PodSticker>
              </PodSpacer>
            )}
          </PodSection>
          {presentation.media.enabled && (
            <>
              {variant === 'tile' && (
                <TileContent>
                  <Image
                    variant={variant}
                    pod={{
                      itemId: pod?.itemId,
                      showSecondaryImage: pod?.showSecondaryImage,
                      dualImageSwipe: pod?.dualImageSwipe,
                      aspect: presentation.media.aspect,
                      padding: presentation.media.padding,
                      fetchPriority: presentation.media.fetchPriority,
                      loading: presentation.media.loading,
                      swatch: presentation.media.swatch,
                      src: presentation.media.src
                          || product.media.images[0].url.replace('<SIZE>', product.media.images[0].sizes[2]),
                      altText: presentation.media.altText,
                      height: presentation.media.height,
                      width: presentation.media.width,
                    }}
                  />
                </TileContent>
              )}
              {variant !== 'tile' && (
                <Image
                  variant={variant}
                  pod={{
                    itemId: pod?.itemId,
                    showSecondaryImage: pod?.showSecondaryImage,
                    dualImageSwipe: pod?.dualImageSwipe,
                    aspect: presentation.media.aspect,
                    padding: presentation.media.padding,
                    fetchPriority: presentation.media.fetchPriority,
                    loading: presentation.media.loading,
                    swatch: presentation.media.swatch,
                    src: presentation.media.src
                          || product.media.images[0].url.replace('<SIZE>', product.media.images[0].sizes[2]),
                    altText: presentation.media.altText,
                    height: presentation.media.height,
                    width: presentation.media.width,
                  }}
                />
              )}
            </>
          )}
          <PodSection columnAlign alignTop>
            {presentation.price.enabled && (
              <PodSpacer minHeight={isMultiColumn ? '4.5rem' : '0'}>
                {/* Product Pod - Product Price */}
                <div className="product-pod-price">
                  <Price
                    channel={channel}
                    itemId={pod.itemId}
                    storeId={storeId}
                    large={false}
                    displayEachUom={false}
                    onSavingsCenterToggle={pod.onSavingsCenterToggle}
                    showPreferredPricingBadge
                    showPreferredPricingBadgeToolTip
                    hidePromotions={hasConditionalPromotions}
                    hideLimitPerOrder={presentation.price.hideLimitPerOrder}
                  />
                </div>
              </PodSpacer>
            )}
            {presentation.promotion.enabled && (
              <PodSpacer minHeight={isMultiColumn ? '2.1rem' : '0'}>
                {/* Product Pod - Product Promotion */}
                <Promotion
                  channel={channel}
                  itemId={pod.itemId}
                  pageType="PLP"
                  hideConditionalPromoDescription
                />
              </PodSpacer>
            )}
            {presentation.header.enabled && (
              <PodSpacer minHeight={isMultiColumn ? '2rem' : '0'} padding={isMultiColumn ? ['TOP'] : []}>
                {/* Product Pod - Product Header */}
                <Header
                  variant={presentation.header.override || variant}
                  isInline={presentation.header.isInline}
                  pod={{
                    itemId: pod.itemId,
                    brandMaxLine: presentation.header.brandMaxLine,
                    brandTitleMaxLine: presentation.header.brandTitleMaxLine,
                    titleMaxLine: presentation.header.titleMaxLine,
                    header: product.identifiers.brandName || presentation.header.header,
                    subheader: product.identifiers.productLabel || presentation.header.subheader,
                    headerWeight: presentation.header.headerWeight,
                    padding: presentation.header.padding,
                  }}
                />
              </PodSpacer>
            )}
          </PodSection>
          <PodFooter>
            <PodSpacer minHeight={isMultiColumn ? 'auto' : '0'}>
              {presentation.cta.enabled && (
                ctaComponent || (
                  <ProductATC
                    itemId={pod.itemId}
                    storeId={storeId}
                    checkGeneric
                    outline
                    quantity={pod.quantity}
                    variant="secondary"
                  />
                )
              )}
            </PodSpacer>
            {/* Slot - Quick View */}
            {children}
          </PodFooter>
        </div>
      )}
    />
  );
};

Pod.propTypes = {
  itemId: string,
  storeId: string,
  index: number,
  channel: string,
  product: shape({
    itemId: string,
    quantity: number
  }),
  presentation: shape({
    media: bool,
    title: bool,
    price: bool,
    promotion: bool,
    cta: oneOfType([func, node])
  }).isRequired,
  variant: string.isRequired,
  children: oneOfType([func, node]),
  ctaComponent: node,
  onSelectChoice: func,
};

Pod.defaultProps = {
  itemId: '',
  storeId: '',
  channel: 'mobile',
  index: undefined,
  product: {},
  children: null,
  ctaComponent: null,
  onSelectChoice: () => {},
};

Pod.dataModel = ProductPodDataModel;

Pod.displayName = 'Pod';
